import React, { useContext, useEffect, useState } from "react";
import { landingPageContext } from "../LandingEdits";

interface Faculty {
  id: string;
  name: string;
  title: string;
  image: string;
}

const AdminFacultyEditor: React.FC = () => {
  const context = useContext(landingPageContext);
  if (!context) throw new Error("Must be used inside LandingEdits");

  const { landingDetails, setLandingDetails } = context;

  const [faculties, setFaculties] = useState<Faculty[]>([]);

  useEffect(() => {
    setFaculties(landingDetails.faculty || []);
  }, [landingDetails.faculty]);

  const updateContext = (updated: Faculty[]) => {
    setFaculties(updated);
    setLandingDetails((prev) => ({ ...prev, faculty: updated }));
  };

  const handleInputChange = (id: string, field: keyof Faculty, value: string) => {
    const updated = faculties.map((f) => (f.id === id ? { ...f, [field]: value } : f));
    updateContext(updated);
  };

  const handleImageUpload = (id: string, file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      handleInputChange(id, "image", base64String);
    };
    reader.readAsDataURL(file);
  };

  const handleAddFaculty = () => {
    const newFaculty: Faculty = {
      id: Date.now().toString(),
      name: "",
      title: "",
      image: "",
    };
    updateContext([...faculties, newFaculty]);
  };

  const handleDeleteFaculty = (id: string) => {
    updateContext(faculties.filter((f) => f.id !== id));
  };

  return (
    <div className="p-6 space-y-6">
      <h2 className="text-2xl font-bold">Faculty Section Editor</h2>
      <button onClick={handleAddFaculty} className="bg-green-600 text-white px-4 py-2 rounded">
        + Add Faculty
      </button>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {faculties.map((faculty) => (
          <div key={faculty.id} className="border rounded-xl p-4 space-y-3 shadow">
            {faculty.image && (
              <img src={faculty.image} alt={faculty.name} className="w-full h-40 object-cover rounded" />
            )}

            <label className="block text-sm font-medium">Upload Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleImageUpload(faculty.id, file);
              }}
              className="w-full text-sm"
            />

            <label className="block text-sm font-medium">Faculty Name</label>
            <input
              type="text"
              placeholder="Name"
              value={faculty.name}
              onChange={(e) => handleInputChange(faculty.id, "name", e.target.value)}
              className="w-full border p-2 rounded"
            />

            <label className="block text-sm font-medium">Title</label>
            <input
              type="text"
              placeholder="Title"
              value={faculty.title}
              onChange={(e) => handleInputChange(faculty.id, "title", e.target.value)}
              className="w-full border p-2 rounded"
            />

            <button
              onClick={() => handleDeleteFaculty(faculty.id)}
              className="bg-red-500 text-white px-4 py-2 rounded w-full"
            >
              Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminFacultyEditor;
